import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChildren,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { TemplatePortalFactoryService } from '../../services/template-portal-factory.service';
import { IconItemParentInterface } from './icon-item/icon-item-parent.interface';
import { IconItemComponent } from './icon-item/icon-item.component';

const iconSize = 16;
const padding = 8;

@Component({
  selector: 'wfm-icons-list',
  templateUrl: './icons-list.component.html',
  styleUrls: ['./icons-list.component.scss']
})
export class IconsListComponent implements AfterViewInit, DoCheck, IconItemParentInterface {
  @Input() allignment = 'left';
  @Input() fillEmptySpace = false;
  @Input() tooltipDelay = 500;

  @Output() onShowTooltip = new EventEmitter<boolean>();

  @ContentChildren(IconItemComponent) icons: QueryList<IconItemComponent>;

  @ViewChild('iconsContainer') container: ElementRef;
  @ViewChild('iconBadge') iconBadge: ElementRef;
  @ViewChild('iconTooltip') tooltipTemplate: TemplateRef<any>;

  visiblesByRealState: Array<IconItemComponent> = [];
  hiddenByRealState: Array<IconItemComponent> = [];
  overflowIcons: Array<IconItemComponent> = [];

  private width = 0;

  private window: Window;
  private overlayRef: OverlayRef;
  private showTimeout;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private templatePortalFactory: TemplatePortalFactoryService
  ) {
    this.window = (this.document as Document).defaultView;
  }

  itemVisibilityChanged() {
    this.resize();
  }

  ngDoCheck(): void {
    const width = (this.container && this.container.nativeElement.offsetWidth) || 0;
    if (width !== this.width) {
      this.resize();
    }
  }

  ngAfterViewInit(): void {
    this.assignIconsParent();
    this.icons.changes.subscribe(() => {
      this.assignIconsParent();
      this.resize();
    });
    this.resize();
  }

  assignIconsParent() {
    this.icons.forEach(icon => {
      icon.parent = this;
    });
  }

  showTooltip() {
    this.showTimeout = this.window.setTimeout(() => {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.iconBadge)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'bottom'
          }
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.close()
      });

      this.overlayRef.attach(
        this.templatePortalFactory.newTemplatePortal(this.tooltipTemplate, this.viewContainerRef, {
          overflowIcons: this.overflowIcons
        })
      );

      this.showTimeout = undefined;

      this.onShowTooltip.emit(true);
    }, this.tooltipDelay);
  }

  hideTooltip() {
    this.window.clearTimeout(this.showTimeout);
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.onShowTooltip.emit(false);
    }
  }

  private timeout = 0;
  private resize() {
    if (this.timeout) return;

    this.timeout = this.window.setTimeout(() => {
      const width = (this.width = (this.container && this.container.nativeElement.offsetWidth) || 0);

      // total number of visible icons for the current width
      let total = Math.floor(width / (iconSize + padding));
      // if remaining space is greater then or equal to a icon size, add one more visible icon
      total += width % (iconSize + padding) >= iconSize ? 1 : 0;

      const icons = (this.icons || []).filter(icon => {
        icon.element.nativeElement.style.display = (this.fillEmptySpace && icon.isHidden && 'none') || '';
        icon.element.nativeElement.style.marginLeft = '';
        return !this.fillEmptySpace || !icon.isHidden;
      });

      if (icons.length <= total) {
        this.visiblesByRealState = icons;
        this.hiddenByRealState = [];
      } else if (total > 0) {
        this.visiblesByRealState = icons.slice(0, total - 1);
        this.hiddenByRealState = icons.slice(total - 1);
      } else {
        this.visiblesByRealState = [];
        this.hiddenByRealState = icons;
      }

      this.overflowIcons = this.hiddenByRealState.filter(icon => !icon.isHidden);
      if (this.overflowIcons.length === 1 && this.hiddenByRealState[0] === this.overflowIcons[0] && total > 0) {
        this.visiblesByRealState.push(this.overflowIcons[0]);
        this.hiddenByRealState = [];
        this.overflowIcons = [];
      }

      this.visiblesByRealState.forEach((item, index) => {
        item.element.nativeElement.style.display = '';
        item.element.nativeElement.style.marginLeft = (!index && '0') || '';
      });
      for (const item of this.hiddenByRealState) {
        item.element.nativeElement.style.display = 'none';
      }

      this.timeout = 0;
    }, 100);
  }
}
